import { Image, Input, Radio, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { CardIsleWidget } from '@hero/components/widgets';
import { CARDISLE_GREETING_CARD, GIFT_MESSAGE_TYPE, MAX_SENDER_NAME_LENGTH } from '@hero/constants';
import { ToggleMenu } from '@hero/molecules';
import { formatNumber, getAddonData } from '@hero/utils';

import { MessageForm } from '../MessageForm';

import * as S from './style';

function MessageExperience({
  messageParams = {},
  shortcutTagsActive,
  onUpdateMessageParams,
  onUpdateOccasion,
  customMessageLength,
  renderCustomizeMessageModal,
  addons = [],
  onUpdateAddons,
  activeGiftMessage,
  setActiveGiftMessage
}) {
  const { t } = useTranslation();
  const { errors } = useValidationErrorsStore();

  return (
    <S.MessageExperienceWrapper>
      <Typography variant="body1Bold" style={{ marginBottom: 16 }}>
        {t('chooseGiftMessageExperience')}:
      </Typography>
      <ToggleMenu
        isActive={activeGiftMessage === GIFT_MESSAGE_TYPE.inputMessage}
        onClick={() => {
          setActiveGiftMessage(GIFT_MESSAGE_TYPE.inputMessage);
        }}
        title={
          <S.ToggleMenuTitleWrapper>
            <Radio
              id={GIFT_MESSAGE_TYPE.inputMessage}
              checked={activeGiftMessage === GIFT_MESSAGE_TYPE.inputMessage}
              label={
                <Typography variant="body2">{t('standardGiftMessageExperienceTitle')}</Typography>
              }
              readOnly
            />
            <Typography variant="caption1" style={{ paddingLeft: 22 }}>
              {t('standardGiftMessageExperienceDesc')}
            </Typography>
          </S.ToggleMenuTitleWrapper>
        }
        hasAccordionIcon={false}
        style={{
          backgroundColor: 'var(--color-bg-weaker)',
          border: 'none',
          borderBottom: '1px solid var(--color-border-strong)'
        }}
      >
        <MessageForm
          messageParams={messageParams}
          shortcutTagsActive={shortcutTagsActive}
          onUpdateMessageParams={params => {
            onUpdateMessageParams(params);
            onUpdateAddons(addons.filter(addon => addon?.type !== CARDISLE_GREETING_CARD));
          }}
          onUpdateOccasion={onUpdateOccasion}
          customMessageLength={customMessageLength}
          renderCustomizeMessageModal={renderCustomizeMessageModal}
        />
      </ToggleMenu>
      <ToggleMenu
        isActive={activeGiftMessage === GIFT_MESSAGE_TYPE.cardMessage}
        onClick={() => {
          setActiveGiftMessage(GIFT_MESSAGE_TYPE.cardMessage);
        }}
        title={
          <S.ToggleMenuTitleWrapper>
            <Radio
              id={GIFT_MESSAGE_TYPE.cardMessage}
              checked={activeGiftMessage === GIFT_MESSAGE_TYPE.cardMessage}
              label={t('greetingCardExperienceTitle')}
              readOnly
            />
            <Typography variant="caption1" style={{ paddingLeft: 22 }}>
              {t('greetingCardExperienceDesc')}
            </Typography>
            {activeGiftMessage === GIFT_MESSAGE_TYPE.cardMessage && (
              <S.GreetingCardRow>
                <Input
                  name="senderName"
                  label={
                    <>
                      {t('senderName')} <S.Required>*</S.Required>
                    </>
                  }
                  value={messageParams.senderName}
                  variant="outlined"
                  onChange={e => onUpdateMessageParams({ senderName: e.target.value })}
                  inputProps={{
                    maxLength: MAX_SENDER_NAME_LENGTH,
                    validations: {
                      required: {
                        required: true,
                        message: t('inputEmptyWarning', { name: t('senderName') })
                      }
                    }
                  }}
                  helperText={
                    <Typography variant="caption1" emphasis="weaker" color="inherit">
                      {t('lengthCharacters', {
                        length: formatNumber(messageParams.senderName?.length),
                        limit: MAX_SENDER_NAME_LENGTH
                      })}
                    </Typography>
                  }
                  error={errors?.warning?.find(e => e.key === 'getSenderNameRule')}
                  style={{ width: 400 }}
                />
              </S.GreetingCardRow>
            )}
            <S.GreetingCardRow>
              {activeGiftMessage !== GIFT_MESSAGE_TYPE.cardMessage && (
                <Image name="greetingCardExperience" width={100} style={{ marginLeft: 16 }} />
              )}

              {activeGiftMessage === GIFT_MESSAGE_TYPE.cardMessage && (
                <CardIsleWidget
                  page="experienceStep"
                  selectedCard={getAddonData(addons, CARDISLE_GREETING_CARD)}
                  updateCard={data => {
                    onUpdateMessageParams({ message: '' });
                    onUpdateAddons([
                      ...addons.filter(addon => addon?.type !== CARDISLE_GREETING_CARD),
                      {
                        type: CARDISLE_GREETING_CARD,
                        data
                      }
                    ]);
                  }}
                />
              )}
            </S.GreetingCardRow>
          </S.ToggleMenuTitleWrapper>
        }
        hasAccordionIcon={false}
        style={{
          backgroundColor: 'var(--color-bg-weaker)',
          border: 'none',
          borderBottom: '1px solid var(--color-border-strong)'
        }}
      />
    </S.MessageExperienceWrapper>
  );
}

MessageExperience.propTypes = {
  messageParams: PropTypes.object,
  shortcutTagsActive: PropTypes.bool,
  onUpdateMessageParams: PropTypes.func,
  onUpdateOccasion: PropTypes.func,
  customMessageLength: PropTypes.number,
  renderCustomizeMessageModal: PropTypes.node,
  addons: PropTypes.array,
  onUpdateAddons: PropTypes.func,
  activeGiftMessage: PropTypes.string,
  setActiveGiftMessage: PropTypes.func
};

export { MessageExperience };
